import { onBeforeUnmount } from 'vue';
import { router } from '@inertiajs/vue3';
import i18n from '@/Utils/i18n.js';

/**
 * Global popstate guard — registered once in app.js BEFORE Inertia initializes,
 * so it fires before Inertia's own popstate handler.
 *
 * Uses a Set so multiple components on the same page (e.g. two forms on
 * /admin/general-settings) each get their own guard.
 */
const activeGuards = new Set();
let globalBypassed = false;

/**
 * Bypass ALL active unsaved-changes guards for the current Inertia visit.
 * Useful when a nested component (e.g. FileManager modal) makes Inertia
 * requests that should not trigger the parent page's guard.
 */
export function bypassUnsavedChanges() {
    globalBypassed = true;
}

export function popstateGuard(event) {
    for (const guard of activeGuards) {
        guard(event);
        // If the guard stopped propagation, don't run the remaining guards —
        // the confirm dialog already handled it.
        if (event.cancelBubble) break;
    }
}

export function useUnsavedChanges(isDirty) {
    let bypassed = false;

    // --- Browser navigation (tab close, URL bar) ---
    const onBeforeUnload = e => {
        if (!isDirty.value || bypassed || globalBypassed) return;
        e.preventDefault();
        e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    // --- Inertia SPA navigation (link clicks, router.visit) ---
    const removeInertiaListener = router.on('before', event => {
        if (!isDirty.value || bypassed || globalBypassed) return;
        // preserveState means the page's Vue state survives this request,
        // so there are no unsaved changes to lose — skip the warning.
        if (event.detail.visit.preserveState) return;
        if (!confirm(i18n.global.t('unsaved_changes_warning'))) {
            event.preventDefault();
        }
    });

    // Reset bypassed after every visit finishes so the guard reactivates
    // if a save fails (e.g. validation error).
    const removeFinishListener = router.on('finish', () => {
        bypassed = false;
        globalBypassed = false;
    });

    // --- Browser back/forward (popstate) ---
    // Inertia's popstate handler bypasses router.on('before') entirely, so we use
    // a global popstate listener registered before Inertia in app.js. When dirty,
    // we immediately stop propagation (preventing Inertia from processing), restore
    // the URL, then show a confirm dialog. If the user confirms leaving, we set
    // bypass and replay the back navigation.
    let currentUrl = window.location.href;

    const removeNavigateListener = router.on('navigate', () => {
        currentUrl = window.location.href;
    });

    const guard = event => {
        if (!isDirty.value || bypassed || globalBypassed) return;

        event.stopImmediatePropagation();
        history.pushState(null, '', currentUrl);

        if (confirm(i18n.global.t('unsaved_changes_warning'))) {
            bypassed = true;
            history.back();
        }
    };
    activeGuards.add(guard);

    // Cleanup on unmount
    onBeforeUnmount(() => {
        window.removeEventListener('beforeunload', onBeforeUnload);
        removeInertiaListener();
        removeFinishListener();
        removeNavigateListener();
        activeGuards.delete(guard);
    });

    function bypass() {
        bypassed = true;
    }

    return { bypass };
}
